/**
 * Part sources.
 *
 * Two implementations behind one interface:
 *
 *   HttpParts   production — parts come from MySQL via the PHP API
 *   DevParts    development — the .LIB files are parsed in the browser
 *
 * Both end up calling the same core generator, so a symbol looks identical
 * either way; only where the bytes come from differs.
 */

import type { ComponentEntity } from '@sircad/core/model.ts';
import {
  generateSymbol, parseSourceLibrary,
  type SourceLibrary, type SourcePart,
} from '@sircad/core/circad/srclib.ts';
import { isKeywordFormat, parseKeywordDocument } from '@sircad/core/circad/parse.ts';
import { isSourceLibrary } from '@sircad/core/circad/srclib.ts';
import { instantiate } from '@sircad/core/geom/transform.ts';
import type { EasyEdaResponse } from '@sircad/core/lib/easyeda.ts';
import { apiGet, apiPost } from './api.ts';

export interface LibraryInfo {
  id: string;
  name: string;
  title: string;
  kind: 'sch' | 'pcb';
  parts: number;
}

export interface PartInfo {
  id: string;
  name: string;
  description: string;
  category: string;
  library: string;
  kind: 'sch' | 'pcb';
  /** Placeable gates. A 7404 has six. */
  sections: number;
  footprint: string | null;
  /** True for a part in one of the user's own libraries, which they may delete.
   *  Built-in libraries are shared stock and are never writable. */
  deletable?: boolean;
}

/** A part to persist into the user's personal library. */
export interface SavedPart {
  kind: 'sch' | 'pcb';
  name: string;
  description?: string;
  category?: string;
  footprint?: string | null;
  sections?: number;
  /** Finished geometry, the same shape a keyword part carries. */
  data: { component: ComponentEntity };
}

export interface SaveResult {
  added: number;
  updated: number;
  skipped: number;
}

export interface PartSource {
  libraries(): Promise<LibraryInfo[]>;
  listParts(libraryId: string): Promise<PartInfo[]>;
  search(query: string): Promise<PartInfo[]>;
  /** Build a placeable component for a part, at the given gate. */
  build(partId: string, section: number): Promise<ComponentEntity | null>;
  /**
   * Resolve a footprint name such as `DIP14` to a placeable PCB component.
   * Used by the schematic-to-board transfer.
   */
  findFootprint(name: string): Promise<ComponentEntity | null>;
  /** Persist parts into the user's personal library. Requires a signed-in user. */
  savePart(parts: SavedPart[]): Promise<SaveResult>;
  /** Remove a part from one of the user's own libraries. Stock parts cannot go. */
  deletePart(partId: string): Promise<void>;
  /** Fetch a part from LCSC/EasyEDA by part number, via the server proxy. */
  lcsc(id: string): Promise<EasyEdaResponse>;
  /** Search LCSC/EasyEDA by keyword, via the server proxy. */
  lcscSearch(query: string): Promise<LcscSearchResult>;
}

/** One result from an LCSC keyword search. */
export interface LcscHit {
  lcsc: string;
  title: string;
  description: string;
  package: string;
}

export interface LcscSearchResult {
  results: LcscHit[];
  /** Which endpoint the results came from. */
  source?: string;
  /** Diagnostics when empty: which endpoints answered, with what status. */
  tried?: { url: string; status: number }[];
  shape?: string[];
}

// ---------------------------------------------------------------------------
// Development: parse the original files in the browser
// ---------------------------------------------------------------------------

/** Decode Windows-1252 bytes. Reading these as UTF-8 corrupts ° and µ. */
function decode1252(buf: ArrayBuffer): string {
  return new TextDecoder('windows-1252').decode(buf);
}

export class DevParts implements PartSource {
  private libs = new Map<string, SourceLibrary>();
  /** Keyword libraries are finished geometry rather than variants. */
  /**
   * Keyword libraries, with the kind declared by the file's own `File Type:`
   * header. SCH.LIB is a keyword library of *symbols*, so assuming keyword
   * means PCB would misfile all 48 of them.
   */
  private keyword = new Map<string, { kind: 'sch' | 'pcb'; comps: ComponentEntity[] }>();
  private info: LibraryInfo[] | null = null;

  async libraries(): Promise<LibraryInfo[]> {
    if (this.info) return this.info;

    const files: string[] = await (await fetch('/devlibs/')).json();
    const out: LibraryInfo[] = [];

    for (const file of files) {
      const buf = await (await fetch(`/devlibs/${file}`)).arrayBuffer();
      const text = decode1252(buf);
      const name = file.replace(/\.lib$/i, '');

      if (isSourceLibrary(text)) {
        const lib = parseSourceLibrary(text, name);
        this.libs.set(name, lib);
        out.push({
          id: name, name, title: lib.title, kind: lib.kind, parts: lib.parts.length,
        });
      } else if (isKeywordFormat(text)) {
        const { document: doc } = parseKeywordDocument(text, name);
        const comps = doc.entities.filter(
          (e): e is ComponentEntity => e.kind === 'component');
        this.keyword.set(name, { kind: doc.kind, comps });
        out.push({
          id: name,
          name,
          title: `${name} (${doc.kind === 'pcb' ? 'PCB' : 'schematic'} library)`,
          kind: doc.kind,
          parts: comps.length,
        });
      }
    }

    out.sort((a, b) => a.name.localeCompare(b.name));
    return (this.info = out);
  }

  async listParts(libraryId: string): Promise<PartInfo[]> {
    await this.libraries();
    const lib = this.libs.get(libraryId);
    if (lib) {
      return lib.parts.map((p) => this.toInfo(libraryId, lib, p));
    }
    const kw = this.keyword.get(libraryId);
    if (!kw) return [];
    return kw.comps.map((c, i) => ({
      id: `${libraryId}#${i}`,
      name: c.name,
      description: c.params[8] ?? c.params[2] ?? '',
      category: 'Library',
      library: libraryId,
      kind: kw.kind,
      sections: 1,
      footprint: null,
      deletable: isPersonalLibrary(libraryId),
    }));
  }

  async search(query: string): Promise<PartInfo[]> {
    await this.libraries();
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const terms = q.split(/\s+/);
    const out: PartInfo[] = [];

    for (const [name, lib] of this.libs) {
      for (const p of lib.parts) {
        const hay = `${p.name} ${p.description} ${p.category}`.toLowerCase();
        if (terms.every((t) => hay.includes(t))) {
          out.push(this.toInfo(name, lib, p));
          if (out.length >= 200) return out;
        }
      }
    }
    for (const [name, kw] of this.keyword) {
      kw.comps.forEach((c, i) => {
        const hay = `${c.name} ${c.params[8] ?? ''}`.toLowerCase();
        if (terms.every((t) => hay.includes(t))) {
          out.push({
            id: `${name}#${i}`, name: c.name, description: c.params[8] ?? '',
            category: 'Library', library: name, kind: kw.kind, sections: 1, footprint: null,
          });
        }
      });
    }
    return out;
  }

  async build(partId: string, section: number): Promise<ComponentEntity | null> {
    await this.libraries();

    // Keyword parts are addressed as `LIB#index`.
    const hash = partId.lastIndexOf('#');
    if (hash !== -1) {
      const libName = partId.slice(0, hash);
      const idx = Number(partId.slice(hash + 1));
      const kw = this.keyword.get(libName);
      const src = kw?.comps[idx];
      // Fresh ids for every child: two footprints sharing pad ids fuse in the
      // netlist, which keys nodes by id.
      if (src) return instantiate(src);
    }

    const slash = partId.lastIndexOf('/');
    const libName = partId.slice(0, slash);
    const partName = partId.slice(slash + 1);
    const lib = this.libs.get(libName);
    if (!lib) return null;
    const part = lib.parts.find((p) => p.name === partName);
    if (!part) return null;

    return generateSymbol(lib, part, 0, { section });
  }

  async findFootprint(name: string): Promise<ComponentEntity | null> {
    await this.libraries();
    const want = name.trim();
    if (want === '' || want === '*' || want === '?') return null;

    // A schematic part may name a *pattern* — `R*`, `DB9*` — because the
    // package is a layout-time choice. Gather every PCB match, then pick the
    // natural-sort winner so `R*` resolves to R300, a real 300-mil resistor,
    // not RV (the Minipot) that a shortest-name order would surface. Mirrors
    // the server's find_footprint so dev and production agree.
    const isPattern = /[*?]/.test(want);
    const rx = isPattern
      ? new RegExp('^' + want.replace(/[.+^${}()|[\]\\]/g, '\\$&')
          .replace(/\*/g, '.*').replace(/\?/g, '.') + '$', 'i')
      : null;
    const matches = (n: string): boolean =>
      rx ? rx.test(n) : n.toUpperCase() === want.toUpperCase();

    const nat = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    type Cand = { name: string; make: () => ComponentEntity | null };
    const cands: Cand[] = [];

    // Keyword libraries first — PCB.LIB holds the finished footprints.
    for (const kw of this.keyword.values()) {
      if (kw.kind !== 'pcb') continue;
      for (const c of kw.comps) {
        if (matches(c.name)) cands.push({ name: c.name, make: () => instantiate(c) });
      }
    }
    // Then the compact PCB libraries, SMD and THD.
    for (const lib of this.libs.values()) {
      if (lib.kind !== 'pcb') continue;
      for (const part of lib.parts) {
        if (matches(part.name)) {
          cands.push({ name: part.name, make: () => generateSymbol(lib, part, 0, { section: 0 }) });
        }
      }
    }

    if (cands.length === 0) return null;
    cands.sort((a, b) => nat.compare(a.name, b.name) || a.name.localeCompare(b.name));
    return cands[0]!.make();
  }

  lcsc(_id: string): Promise<EasyEdaResponse> {
    return Promise.reject(new Error('LCSC import needs the server; run the built app.'));
  }

  lcscSearch(_query: string): Promise<LcscSearchResult> {
    return Promise.reject(new Error('LCSC search needs the server; run the built app.'));
  }

  async savePart(parts: SavedPart[]): Promise<SaveResult> {
    await this.libraries();
    let added = 0;
    let updated = 0;
    for (const p of parts) {
      const libName = p.kind === 'sch' ? 'My Symbols' : 'My Footprints';
      let kw = this.keyword.get(libName);
      if (!kw) {
        kw = { kind: p.kind, comps: [] };
        this.keyword.set(libName, kw);
        this.info?.push({ id: libName, name: libName, title: libName, kind: p.kind, parts: 0 });
      }
      const i = kw.comps.findIndex((c) => c.name.toUpperCase() === p.name.toUpperCase());
      if (i >= 0) { kw.comps[i] = p.data.component; updated++; }
      else { kw.comps.push(p.data.component); added++; }
      const info = this.info?.find((x) => x.id === libName);
      if (info) info.parts = kw.comps.length;
    }
    return { added, updated, skipped: 0 };
  }

  /** Remove a part from an in-memory personal library. Dev ids are `lib#index`. */
  async deletePart(partId: string): Promise<void> {
    const hash = partId.lastIndexOf('#');
    const libName = hash >= 0 ? partId.slice(0, hash) : partId;
    const index = hash >= 0 ? Number(partId.slice(hash + 1)) : -1;
    if (!isPersonalLibrary(libName)) {
      throw new Error('Only parts in your own libraries can be deleted.');
    }
    const kw = this.keyword.get(libName);
    if (!kw || !Number.isInteger(index) || index < 0 || index >= kw.comps.length) {
      throw new Error('Part not found.');
    }
    kw.comps.splice(index, 1);
    const info = this.info?.find((x) => x.id === libName);
    if (info) info.parts = kw.comps.length;
  }

  private toInfo(libId: string, lib: SourceLibrary, p: SourcePart): PartInfo {
    return {
      id: `${libId}/${p.name}`,
      name: p.name,
      description: p.description,
      category: p.category,
      library: libId,
      kind: lib.kind,
      sections: p.variants.reduce((n, v) => Math.max(n, v.sections), 1),
      footprint: p.variants.find((v) => v.footprint)?.footprint ?? null,
    };
  }
}

// ---------------------------------------------------------------------------
// Production: the PHP API
// ---------------------------------------------------------------------------

interface ApiPartRow {
  id: number;
  name: string;
  description: string;
  category: string;
  library: string;
  kind: 'sch' | 'pcb';
  sections: number;
  footprint: string | null;
  /** 1 for a stock library, 0 for one of the user's own. */
  builtin?: number;
}

/** The libraries `savePart` writes into, and so the only ones a user may edit. */
function isPersonalLibrary(name: string): boolean {
  return name === 'My Symbols' || name === 'My Footprints';
}

export class HttpParts implements PartSource {
  private get = apiGet;

  async libraries(): Promise<LibraryInfo[]> {
    const rows = await this.get<{ id: number; name: string; title: string;
      kind: 'sch' | 'pcb'; parts: number }[]>('libraries');
    for (const r of rows) this.kinds.set(String(r.id), r.kind);
    return rows.map((r) => ({
      id: String(r.id), name: r.name, title: r.title, kind: r.kind, parts: r.parts,
    }));
  }

  /** Library kinds, so a row missing `kind` can fall back to its library. */
  private kinds = new Map<string, 'sch' | 'pcb'>();

  async listParts(libraryId: string): Promise<PartInfo[]> {
    const rows = await this.get<ApiPartRow[]>('library_parts', { id: libraryId });
    // Defaulting a missing kind to 'sch' silently mislabelled every footprint
    // as a symbol. Fall back to the library's own kind, which we already know.
    const fallback = this.kinds.get(libraryId) ?? 'sch';
    return rows.map((r) => ({
      ...r,
      id: String(r.id),
      library: libraryId,
      kind: r.kind ?? fallback,
      deletable: Number(r.builtin ?? 1) === 0,
    }));
  }

  async search(query: string): Promise<PartInfo[]> {
    const rows = await this.get<ApiPartRow[]>('search_parts', { q: query });
    return rows.map((r) => ({
      ...r, id: String(r.id), deletable: Number(r.builtin ?? 1) === 0,
    }));
  }

  async build(partId: string, section: number): Promise<ComponentEntity | null> {
    const row = await this.get<{
      format: 'source' | 'keyword';
      name: string;
      description: string;
      category: string;
      data: { variants?: unknown[]; component?: ComponentEntity };
      patterns: Record<string, { entities: unknown[]; block: unknown[] }> | null;
      aliases: { pattern: string; target: string }[];
    }>('part', { id: partId });

    if (row.format === 'keyword') {
      return row.data.component ? instantiate(row.data.component) : null;
    }

    // Reassemble just enough of a SourceLibrary for the generator: it needs the
    // pattern glyphs, and the part's own variants.
    const patterns = new Map<string, { name: string; entities: never[]; block: never[] }>();
    for (const [k, v] of Object.entries(row.patterns ?? {})) {
      patterns.set(k, { name: k, entities: v.entities as never[], block: v.block as never[] });
    }
    const lib = {
      name: '', title: '', kind: 'sch' as const, aliases: row.aliases,
      patterns, parts: [], warnings: [],
    } as unknown as SourceLibrary;

    const part = {
      name: row.name,
      description: row.description,
      category: row.category,
      variants: row.data.variants ?? [],
    } as unknown as SourcePart;

    return generateSymbol(lib, part, 0, { section });
  }

  async findFootprint(name: string): Promise<ComponentEntity | null> {
    const rows = await this.get<{ id: number; name: string }[]>(
      'find_footprint', { name });
    const hit = rows[0];
    if (!hit) return null;
    return this.build(String(hit.id), 0);
  }

  async savePart(parts: SavedPart[]): Promise<SaveResult> {
    return apiPost<SaveResult>('save_parts', { parts });
  }

  async deletePart(partId: string): Promise<void> {
    await apiPost('delete_part', { id: Number(partId) });
  }

  async lcsc(id: string): Promise<EasyEdaResponse> {
    return this.get<EasyEdaResponse>('lcsc', { id });
  }

  async lcscSearch(query: string): Promise<LcscSearchResult> {
    return this.get<LcscSearchResult>('lcsc_search', { q: query });
  }
}
